import { AdviseSupplierLineItem, SupplierLineItemsReplacement } from '../../../../types/advise';
import { LINE_ITEMS_LIMIT, LineField, lineFieldError } from './currentSupplierLimits';

export interface DraftLine {
  key: string;
  id?: string;
  in_scope: boolean;
  category: string;
  description: string;
  quantity: string;
  unit_cost: string;
  monthly_cost: string;
}

export type DraftLineErrors = Partial<Record<LineField | 'category', string>>;

const TYPED_FIELDS: LineField[] = ['description', 'quantity', 'unit_cost', 'monthly_cost'];
const AMOUNT_FIELDS = ['quantity', 'unit_cost', 'monthly_cost'] as const;

const textOf = (value?: number | string | null) => (value == null ? '' : String(value));

export const blankLine = (key: string): DraftLine => ({
  key,
  in_scope: true,
  category: '',
  description: '',
  quantity: '',
  unit_cost: '',
  monthly_cost: ''
});

const draftLineOf = (line: AdviseSupplierLineItem): DraftLine => ({
  key: line.id,
  id: line.id,
  in_scope: line.in_scope,
  category: line.category,
  description: textOf(line.description),
  quantity: textOf(line.quantity),
  unit_cost: textOf(line.unit_cost),
  monthly_cost: textOf(line.monthly_cost)
});

export const draftLinesOf = (lines: AdviseSupplierLineItem[]): DraftLine[] => [
  ...lines.map(draftLineOf),
  blankLine('blank')
];

export const isBlankLine = (line: DraftLine) =>
  !line.category && TYPED_FIELDS.every((field) => line[field].trim() === '');

const amountOf = (field: (typeof AMOUNT_FIELDS)[number], text: string) =>
  text.trim() === '' || lineFieldError(field, text) ? null : Number(text);

const PLACES = 4;
const PER_CENT = BigInt(10 ** (2 * PLACES - 2));
const HALF_CENT = PER_CENT / BigInt(2);

// Floating point turns 1 × 1.005 into 1.00499…, so the typed decimals are multiplied as whole ten-thousandths.
const tenThousandths = (text: string) => {
  const [whole, fraction = ''] = text.trim().split('.');
  return BigInt(`${whole || '0'}${fraction.padEnd(PLACES, '0')}`);
};

const centsText = (cents: bigint) => {
  const digits = cents.toString().padStart(3, '0');
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
};

const monthlyCostFrom = (line: DraftLine) => {
  if (amountOf('quantity', line.quantity) === null || amountOf('unit_cost', line.unit_cost) === null) {
    return line.monthly_cost;
  }
  const product = tenThousandths(line.quantity) * tenThousandths(line.unit_cost);
  return centsText((product + HALF_CENT) / PER_CENT);
};

export const withLineEdit = (line: DraftLine, field: LineField, text: string): DraftLine => {
  const edited = { ...line, [field]: text };
  if (field !== 'quantity' && field !== 'unit_cost') return edited;
  return { ...edited, monthly_cost: monthlyCostFrom(edited) };
};

export const lineErrors = (line: DraftLine): DraftLineErrors => {
  if (isBlankLine(line)) return {};
  const errors: DraftLineErrors = line.category ? {} : { category: 'Select a category.' };
  TYPED_FIELDS.forEach((field) => {
    const error = lineFieldError(field, line[field]);
    if (error) errors[field] = error;
  });
  return errors;
};

const typedLines = (lines: DraftLine[]) => lines.filter((line) => !isBlankLine(line));

export const draftLinesError = (lines: DraftLine[]) => {
  const typed = typedLines(lines);
  if (typed.length > LINE_ITEMS_LIMIT) return `Enter at most ${LINE_ITEMS_LIMIT} line items.`;
  if (typed.some((line) => Object.keys(lineErrors(line)).length > 0)) return 'Fix the line items marked in red.';
  return null;
};

const lineInput = (line: DraftLine) => ({
  ...(line.id ? { id: line.id } : {}),
  in_scope: line.in_scope,
  category: line.category,
  description: line.description.trim() || null,
  quantity: amountOf('quantity', line.quantity),
  unit_cost: amountOf('unit_cost', line.unit_cost),
  monthly_cost: amountOf('monthly_cost', line.monthly_cost)
});

export const linesReplacement = (lines: DraftLine[], version: string): SupplierLineItemsReplacement => ({
  version,
  line_items: typedLines(lines).map(lineInput)
});

const centsOf = (line: DraftLine) => Math.round((amountOf('monthly_cost', line.monthly_cost) ?? 0) * 100);

export const draftTotals = (lines: DraftLine[]) => {
  const typed = typedLines(lines);
  const sum = (inScope: boolean) =>
    typed.filter((line) => line.in_scope === inScope).reduce((cents, line) => cents + centsOf(line), 0) / 100;
  return { inScope: sum(true), outOfScope: sum(false) };
};

// Saved lines are always valid, so a draft the API would refuse has changed even where it would send the same.
export const isLinesDraftChanged = (saved: AdviseSupplierLineItem[], lines: DraftLine[]) =>
  draftLinesError(lines) !== null ||
  JSON.stringify(linesReplacement(draftLinesOf(saved), '').line_items) !==
    JSON.stringify(linesReplacement(lines, '').line_items);
